import { useEffect, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../../types/plans'
import type { DiagnocatNasPrefetchPhase } from './domain/diagnocatWorkflow'
import { listPatientMediaSources, type PatientMediaSourceSnapshot } from './patientMedia'

const PREFETCH_RETRIES = 2
const RETRY_DELAY_MS = 700

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function countNasImages(sources: PatientMediaSourceSnapshot[]): number {
  return sources.reduce((total, snap) => {
    const linkedAssets = snap.linked ? snap.assets.length : 0
    const candidateAssets = snap.candidates.reduce(
      (sum, candidate) => sum + (candidate.asset_count ?? 0),
      0,
    )
    return total + linkedAssets + candidateAssets
  }, 0)
}

export function usePatientNasPhotoPrefetch({
  bootstrap,
  session,
  patientId,
  enabled,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  patientId: string
  enabled: boolean
}) {
  const [phase, setPhase] = useState<DiagnocatNasPrefetchPhase>('idle')
  const [photoCount, setPhotoCount] = useState(0)
  const [sources, setSources] = useState<PatientMediaSourceSnapshot[]>([])

  useEffect(() => {
    if (!enabled || !patientId) {
      setPhase('idle')
      return
    }
    let cancelled = false
    setPhase((current) => (current === 'ready' ? current : 'loading'))
    void (async () => {
      for (let attempt = 0; attempt <= PREFETCH_RETRIES; attempt += 1) {
        try {
          const data = await listPatientMediaSources({ bootstrap, session, patientId })
          if (cancelled) return
          setSources(data)
          setPhotoCount(countNasImages(data))
          setPhase('ready')
          return
        } catch {
          if (cancelled) return
          if (attempt < PREFETCH_RETRIES) {
            await delay(RETRY_DELAY_MS * (attempt + 1))
            continue
          }
          setPhase((current) => (current === 'ready' ? current : 'error'))
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [bootstrap, session, patientId, enabled])

  return { phase, photoCount, sources }
}
